import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import IconSvg from 'src/components/common/ui/IconSvg';
import Pricing from 'src/components/pages/home/Pricing';
import PricingMobile from 'src/components/pages/home/PricingMobile';
import TestimonialCarousel from 'src/components/pages/home/Testimonial';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when clicking outside or pressing Escape key
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    // Disable body scroll when menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'auto';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <div className="lg:w-[1317px] lg:mx-auto lg:pt-10 pt-5 lg:pb-20 pb-0 lg:px-0 px-4">
        {/* Header */}
        <div className="flex items-center justify-between relative">

          <IconSvg icon="appLogo" className="cursor-pointer lg:h-auto h-8" onClick={() => navigate('/')} />

          {/* Mobile Menu Button */}
          <div
            className="lg:hidden block cursor-pointer z-50"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <></>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="33"
                viewBox="0 0 32 33"
                fill="none"
                className="transition-transform duration-300"
              >
                <path
                  d="M4 23.1666H28M4 16.5H28M4 9.83331H28"
                  stroke="black"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>

          {/* Mobile Menu Overlay and Content */}
          <div className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {/* Background Overlay */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>

            {/* Menu Content */}
            <div
              ref={mobileMenuRef}
              className={`absolute top-0 right-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
              <div className="flex flex-col h-full p-6">
                {/* Close Button */}
                <div className="flex justify-end mb-8">
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="black"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Navigation Links */}
                <div className="flex flex-col space-y-6 mb-8">
                  <p className="text-xl font-medium text-grayscale-800 cursor-pointer font-inter py-2 border-b border-gray-100">
                    About
                  </p>
                  <p className="text-xl font-medium text-grayscale-800 cursor-pointer font-inter py-2 border-b border-gray-100">
                    Testimonial
                  </p>
                  <p className="text-xl font-medium text-grayscale-800 cursor-pointer font-inter py-2 border-b border-gray-100" onClick={() => navigate('/contact-us')}>
                    Contact
                  </p>
                </div>

                {/* Auth Buttons */}
                <div className="mt-auto mb-8">
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/signin');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-6 py-4 rounded-lg cursor-pointer text-grayscale-100 bg-black text-lg font-medium font-inter mb-4 transition-transform hover:scale-105"
                  >
                    Sign in
                  </button>
                  <button
                    onClick={() => {
                      navigate('/signup');
                      setIsMobileMenuOpen(false);
                    }}
                    type="button"
                    className="w-full px-6 py-4 rounded-lg cursor-pointer text-grayscale-100 bg-[#6277FF] text-lg font-medium font-inter transition-transform hover:scale-105"
                  >
                    Sign up
                  </button>
                </div>

                {/* Footer Note */}
                <div className="text-center text-gray-500 text-sm">
                  © 2024 Adeasy AI. All rights reserved.
                </div>
              </div>
            </div>
          </div>

          <div className="lg:flex hidden items-center space-x-32">
            <div className="flex items-center space-x-20">
              <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter">
                About
              </p>
              <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter">
                Testimonial
              </p>
              <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter" onClick={() => navigate('/contact-us')}>
                Contact
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => navigate('/signin')}
                className="px-6 py-3 rounded-lg cursor-pointer text-grayscale-100 bg-black text-lg font-medium font-inter w-[154px]"
              >
                Sign in
              </button>
              <button
                onClick={() => navigate('/signup')}
                type="button"
                className="px-6 py-3 rounded-lg cursor-pointer text-grayscale-100 bg-[#6277FF] text-lg font-medium font-inter w-[154px]"
              >
                Sign up
              </button>
            </div>
          </div>
        </div>

        {/* Rest of your content remains exactly the same */}
        {/* Banner */}
        <div className="mt-24 relative lg:block hidden">
          <div className="flex flex-col absolute top-[32%] left-[10%]">
            <p className="font-normal text-[40px] text-white font-inter leading-tight">
              Optimize Your ads With <br />adeasy ai{' '}
            </p>
            <p className="font-normal text-lg text-grayscale-400 font-inter my-5">
              Adivo simplifies the steps to a winning ad campaign.
              <br /> Using proven AI optimization to take the process {' '}
              <br /> out of your hands. So you can benefit from a{' '}
              <br /> successful ad campaign without hiring an expensive{' '}
              <br /> team.{' '}
            </p>
            <a
              href="https://adivo.webflow.io/#"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg cursor-pointer text-grayscale-100 bg-[#6277FF] text-xs font-medium font-inter w-[160px] underline underline-offset-2 flex items-center justify-center"
            >
              14 DAYS FREE TRIAL
            </a>
          </div>
          <img src="/assets/images/banner.png" className="" />
        </div>

        <div className="lg:hidden block mt-10 relative">
          {/* Background Image */}
          <img
            src="/assets/images/home1.png"
            className="w-full h-auto object-cover rounded-2xl"
          />

          {/* Overlay Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-end text-center p-6 mb-44">
            <p className="font-bold text-[28px] sm:text-[36px] lg:text-[40px] text-white font-inter leading-tight">
              Optimize Your Ads With <br /> Adeasy AI
            </p>

            <p className="font-normal text-base sm:text-lg text-gray-300 font-inter my-5 max-w-md">
              Adivo simplifies the steps to a winning ad campaign.
              <br /> Using proven AI optimization to take the process
              <br /> out of your hands. So you can benefit from a
              <br /> successful ad campaign without hiring an expensive
              <br /> team.
            </p>

            <a
              href="https://adivo.webflow.io/#"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-lg cursor-pointer text-white bg-[#6277FF] text-sm font-medium font-inter shadow-lg hover:bg-[#5063d6] transition"
            >
              14 DAYS FREE TRIAL
            </a>
          </div>
        </div>

        {/* Banner2 */}
        <div className="mt-24 relative w-full lg:block hidden">
          <div className="flex flex-col w-full items-center justify-center pt-20 absolute">
            <p className="font-bold text-[34px] text-grayscale-100 font-inter">Tested, Trusted</p>
            <p className="font-bold text-[34px] text-grayscale-100 font-inter">and Valued by many</p>
            <p className="font-normal text-lg text-grayscale-400 font-inter mt-4">
              Proven results across industries — from startups to enterprise — with
              <br />
              measurable ROI and growth, backed by real customer success stories
              <br />
              <span className="block text-center">
                that showcase the impact we deliver every day.
              </span>
            </p>

            <button
              type="button"
              className="px-6 py-3 rounded-lg cursor-pointer text-grayscale-100 mt-6 bg-[#6277FF] text-lg font-medium font-inter w-[200px]"
              onClick={() => navigate("/signup")}
            >
              Start Trial
            </button>
          </div>
          <img src="/assets/images/banner2.png" className="" />
        </div>

        <div className="lg:hidden block mt-10 relative">
          {/* Background Image */}
          <img
            src="/assets/images/home2.png"
            className="w-full h-auto object-cover rounded-2xl"
          />

          {/* Overlay Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="font-bold text-[28px] sm:text-[34px] text-white font-inter">
              Tested, Trusted
            </p>
            <p className="font-bold text-[28px] sm:text-[34px] text-white font-inter mb-4">
              and Valued by Many
            </p>

            <p className="font-normal text-base sm:text-lg text-gray-300 font-inter max-w-xl leading-relaxed">
              Proven results across industries — from startups to enterprise —
              with measurable ROI and growth, backed by real customer success stories
              that showcase the impact we deliver every day.
            </p>

            <button
              type="button"
              className="mt-6 px-6 py-3 rounded-lg cursor-pointer text-white bg-[#6277FF] hover:bg-[#5063d6] text-base font-medium font-inter w-[200px] shadow-lg transition"
              onClick={() => navigate("/signup")}
            >
              Start Trial
            </button>
          </div>
        </div>

        {/* Testimonials */}
        <TestimonialCarousel />
        <Pricing />
        <PricingMobile />
      </div>

      {/* Footer */}
      <div
        className="lg:h-[100px] bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/images/footer.png')",
        }}
      >
        <div className="lg:w-[1317px] lg:mx-auto flex items-center justify-between py-6 lg:px-0 px-4">
          {/* Logo */}
          <img
            src="/assets/images/footer-logo.png"
            onClick={() => navigate('/')}
            alt="Logo"
            className="lg:h-12 h-8 cursor-pointer"
          />

          {/* Navigation Links */}
          <div className="flex items-center lg:space-x-16 space-x-8">
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter">
              About
            </p>
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter">
              Testimonial
            </p>
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter"
              onClick={() => navigate('/contact-us')}>
              Contact
            </p>
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter"
              onClick={() => navigate('/privacy-policy')}
            >
              Privacy Policy
            </p>
          </div>

          {/* Sign In Button */}
          <div className="lg:block hidden">
            <button
              type="button"
              onClick={() => navigate("/signin")}
              className="px-6 py-2 rounded-lg bg-white text-black text-[18px] font-medium font-inter shadow-md hover:bg-gray-200 transition"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center py-4 space-x-2">
        <p className="font-inter lg:text-lg text-sm lg:font-medium font-normal text-black">
          Copyright 2024
        </p>
        <IconSvg icon='copyright' />
      </div>
    </>
  );
};

export default Home;